import { useEffect, useState, type ReactNode } from 'react'
import { Loader2, Pencil, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { dueDateProblem, earliestDueDate } from '@/lib/dueDate'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
// The same "today" overdue is judged by; the server re-checks in the tenant's zone.
import { todayIso } from '@/lib/maintenanceStatus'

/**
 * A task's due date, editable in place: whatever the table shows for it
 * (`children`), plus a "Change due date" control that opens a date picker with
 * Save. Used by every list of tasks (Tasks on Cleaning, Equipment and
 * Inventory, and Pending Actions), overdue tasks above all: moving the date is
 * how one is put back on schedule.
 *
 * Held to the server's rules before it is sent (today or later, never before
 * the early start; see lib/dueDate), and the server's own refusal shows beside
 * the date. Read-only (just `children`) without `onSave`: a booking, a
 * completed task, or someone who may not edit tasks.
 */
export function DueDateEditor({
  dueDate,
  earlyStart,
  subject,
  onSave,
  children,
}: {
  /** `YYYY-MM-DD`. */
  dueDate: string
  earlyStart?: string | null
  /** What the task is on, for the controls' accessible names. */
  subject: string
  onSave?: (dueDate: string) => Promise<void>
  children: ReactNode
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(dueDate)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!editing) setDraft(dueDate)
  }, [dueDate, editing])

  if (!onSave) return <>{children}</>

  if (!editing) {
    return (
      <span className='inline-flex items-start gap-1.5'>
        {children}
        <button
          type='button'
          title='Change due date'
          aria-label={`Change due date of ${subject}`}
          onClick={e => {
            e.stopPropagation()
            setError(null)
            setEditing(true)
          }}
          className='mt-0.5 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700'
        >
          <Pencil className='h-3.5 w-3.5' />
        </button>
      </span>
    )
  }

  const today = todayIso()
  const unchanged = draft === dueDate
  const problem = unchanged ? null : dueDateProblem({ today, dueDate: draft, current: dueDate, earlyStart })
  const save = async () => {
    if (unchanged || problem || saving) return
    setSaving(true)
    setError(null)
    try {
      await onSave(draft)
      setEditing(false)
    } catch (err) {
      setError(apiErrorMessage(err, 'The due date could not be changed.'))
    } finally {
      setSaving(false)
    }
  }
  const message = error ?? problem

  return (
    <div className='space-y-1 whitespace-normal' onClick={e => e.stopPropagation()}>
      <div className='flex items-center gap-1'>
        <input
          type='date'
          value={draft}
          min={earliestDueDate(today, earlyStart)}
          disabled={saving}
          autoFocus
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void save()
            }
            if (e.key === 'Escape') setEditing(false)
          }}
          aria-label={`New due date for ${subject}`}
          className={cn(
            'h-8 rounded-md border bg-background px-2 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-primary/30',
            message && 'border-red-400',
          )}
        />
        <button
          type='button'
          onClick={() => void save()}
          disabled={unchanged || !!problem || saving}
          className='inline-flex h-8 items-center rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground'
        >
          {saving ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : 'Save'}
        </button>
        <button
          type='button'
          title='Cancel'
          aria-label='Cancel changing the due date'
          onClick={() => setEditing(false)}
          disabled={saving}
          className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700'
        >
          <X className='h-3.5 w-3.5' />
        </button>
      </div>
      {message && <p className='max-w-56 text-[11px] font-medium text-red-600'>{message}</p>}
    </div>
  )
}
